import { create } from "zustand";
import { Node, Beam } from "../types/nnstructural2D";
import {
  createNodeAtBeamCenter,
  generateBeamLabel,
  findBeamAtPosition3D,
  createNodeAtBeamPosition,
} from "../utilities/BeamCenterUtils";
import { addData } from "../services/centralized";
import { usePropertyStore } from "./usePropertyStore";
import { useTrussStore } from "../hooks/useTrussManagement";
import { useBeamTrussStore } from "../hooks/useBeamTrussManagement";

interface TrussPreset {
  id: string;
  name: string;
  type: "regular" | "perpendicular";
  rotationValues: { X: number; Y: number; Z: number };
  perpendicularValues: { X: number; Y: number; Z: number };
  nodeIds?: string[];
}

interface PasteParams {
  x: number;
  y: number;
  z: number;
  copies: number;
}

interface ModelState {
  projectName: string;
  nodes: Node[];
  beams: Beam[];
  nextNodeId: number;
  nextBeamId: number;
  selectedNodeIds: string[];
  selectedBeamIds: string[];
  hoveredBeam: string | null;
  showBeamLabels: boolean;
  selectedElements: string[];
  isBeamCreationMode: boolean;
  beamCreationFirstNodeId: string | null;
  statusMessage: string | null;
  isPrepMode: boolean;
  referenceBeamId: string | null;
  isDivideBeamMode: boolean;
  // Functions
  setProjectName: (name: string) => void;
  selectElement: (id: string) => void;
  clearSelection: () => void;
  updateNode: (nodeId: string, updates: Partial<Node>) => void;
  updateElement: (elementId: string, updates: Partial<any>) => void;
  startBeamCreation: () => void;
  cancelBeamCreation: () => void;
  setBeamCreationFirstNode: (nodeId: string) => void;
  // Prep Mode functions
  startPrepMode: () => void;
  cancelPrepMode: () => void;
  setReferenceBeam: (beamId: string) => void;

  startDivideBeamMode: () => void;
  cancelDivideBeamMode: () => void;

  // Legacy compatibility functions
  addNode: (coordinates: { x: number; y: number; z: number }) => void;
  selectNode: (nodeId: string) => void;
  selectBeam: (beamId: string) => void;
  selectBeamOnly: (beamId: string) => void;
  setHoveredBeam: (beamId: string | null) => void;
  toggleBeamLabels: () => void;
  addNodeAtBeamCenter: (beamId: string, clickPoint?: any) => boolean;
  addBeamWithLabel: (
    startNodeId: string,
    endNodeId: string,
    label?: string
  ) => void;
  restoreDefaultColors: () => void;

  // Beam operations
  addBeam: (startNodeId: string, endNodeId: string) => void;
  deleteBeam: (beamId: string) => void;
  deleteNode: (nodeId: string) => void;
  splitBeamAtCenter: (beamId: string) => any;
  divideBeamAtMidpoint: (beamId: string) => boolean;
  getBeamMidpoint3D: (
    beamId: string
  ) => { x: number; y: number; z: number } | null;
  findBeamAtPosition3D: (
    position: { x: number; y: number; z: number },
    tolerance?: number
  ) => Beam | null;
  addNodeOnBeam: (
    clickPoint: { x: number; y: number; z: number },
    tolerance?: number
  ) => boolean;

  // Selection operations
  toggleNodeSelection: (nodeId: string) => void;
  toggleBeamSelection: (beamId: string) => void;
  selectMultipleNodes: (nodeIds: string[]) => void;
  selectMultipleBeams: (beamIds: string[]) => void;

  // Copy-paste operations
  copySelected: () => void;
  pasteWithOffset: (params: PasteParams) => void;

  // Beam label operations
  setBeamLabel: (beamId: string, label: string) => void;
  updateBeamLabel: (beamId: string, label: string) => void;

  // Beam properties operations
  updateBeamProperties: (
    beamId: string,
    properties: { material?: string; color?: string; label?: string }
  ) => void;

  // Copy-paste state
  copiedNodes: Node[];
  copiedBeams: Beam[];

  // Bulk operations
  setNodes: (nodes: Node[]) => void;
  setBeams: (beams: Beam[]) => void;
  updateBeamsWithoutAutoApply: (beams: Beam[]) => void;

  // Beam regeneration
  regenerateBeams: (movedNodeId?: string) => {
    success: boolean;
    message: string;
    connectedBeams: number;
  };

  // Perpendicular beam operations
  createPerpendicularBeams: (
    referenceBeamId: string,
    nodeIds: string[]
  ) => Beam[];

  clearAll: () => void;
}

export const useModelStore = create<ModelState>((set, get) => {
  // Helper function to find lowest available node number
  const findLowestAvailableNodeNumber = (nodes: Node[]): number => {
    // Get all used node numbers
    const usedNumbers = new Set(nodes.map((node) => parseInt(node.id)));

    // Find first available number starting from 1
    let number = 1;
    while (usedNumbers.has(number)) {
      number++;
    }
    return number;
  };

  return {
    projectName: "Untitled Project",
    nodes: [],
    beams: [],
    nextNodeId: 1,
    nextBeamId: 1,
    hoveredBeam: null,
    showBeamLabels: false,
    isBeamCreationMode: false,
    beamCreationFirstNodeId: null,
    statusMessage: null,
    // Prep Mode state
    isPrepMode: false,
    referenceBeamId: null,
    isDivideBeamMode: false,

    setProjectName: (name: string) => set({ projectName: name }),

    // Prep Mode functions
    startPrepMode: () =>
      set(() => ({
        isPrepMode: true,
        isBeamCreationMode: false,
        beamCreationFirstNodeId: null,
        referenceBeamId: null,
        selectedNodeIds: [],
        selectedBeamIds: [],
        statusMessage: "Select a reference beam",
        nodes: get().nodes.map((node) => ({
          ...node,
          isSelected: false,
          color: "#000000",
        })),
        beams: get().beams.map((beam) => ({
          ...beam,
          isSelected: false,
          color: "#4dabf7",
        })),
      })),

    cancelPrepMode: () =>
      set(() => ({
        isPrepMode: false,
        referenceBeamId: null,
        statusMessage: null,
        nodes: get().nodes.map((node) => ({
          ...node,
          isSelected: false,
          color: "#000000",
        })),
        beams: get().beams.map((beam) => ({
          ...beam,
          isSelected: false,
          color: "#4dabf7",
        })),
      })),

    setReferenceBeam: (beamId: string) =>
      set((state) => ({
        referenceBeamId: beamId,
        beams: state.beams.map((beam) => ({
          ...beam,
          color: beam.id === beamId ? "#ffa500" : "#4dabf7", // Orange for reference beam
          isSelected: beam.id === beamId,
        })),
        statusMessage: "Select node(s) for perpendicular beam",
      })),

    startDivideBeamMode: () => {
      set({
        isDivideBeamMode: true,
        isBeamCreationMode: false,
        beamCreationFirstNodeId: null,
        isPrepMode: false,
        referenceBeamId: null,
        selectedNodeIds: [],
        selectedBeamIds: [],
        statusMessage: "Click a beam to divide it at midpoint",
        nodes: get().nodes.map((node) => ({
          ...node,
          isSelected: false,
          color: "#000000",
        })),
        beams: get().beams.map((beam) => ({
          ...beam,
          isSelected: false,
          color: "#4dabf7",
        })),
      });
    },

    cancelDivideBeamMode: () => {
      set({
        isDivideBeamMode: false,
        statusMessage: null,
        nodes: get().nodes.map((node) => ({
          ...node,
          isSelected: false,
          color: "#000000",
        })),
        beams: get().beams.map((beam) => ({
          ...beam,
          isSelected: false,
          color: "#4dabf7",
        })),
      });
    },
    // Selection state
    selectedNodeIds: [],
    selectedBeamIds: [],

    // Beam creation state management
    startBeamCreation: () =>
      set({
        isBeamCreationMode: true,
        beamCreationFirstNodeId: null,
        nodes: get().nodes.map((node) => ({
          ...node,
          color: "#000000", // Reset all nodes to black (default color)
        })),
        statusMessage: "Click first node",
      }),

    cancelBeamCreation: () =>
      set({
        isBeamCreationMode: false,
        beamCreationFirstNodeId: null,
        nodes: get().nodes.map((node) => ({
          ...node,
          color: "#000000", // Reset all nodes to black (default color)
        })),
        statusMessage: null,
      }),

    setBeamCreationFirstNode: (nodeId: string) =>
      set((state) => ({
        beamCreationFirstNodeId: nodeId,
        nodes: state.nodes.map((node) => ({
          ...node,
          color: node.id === nodeId ? "#FFA500" : "#000000", // Set clicked node to orange, others to black
        })),
        statusMessage: "Click second node",
      })),

    // Copy-paste state
    copiedNodes: [],
    copiedBeams: [],

    // Color management
    restoreDefaultColors: () =>
      set((state) => {
        // Clear selection arrays first
        const clearedState = {
          selectedNodeIds: [],
          selectedBeamIds: [],
          nodes: state.nodes.map((node) => ({
            ...node,
            isSelected: false,
            color: "#000000", // Default node color
          })),
          beams: state.beams.map((beam) => ({
            ...beam,
            isSelected: false,
            color: "#4dabf7", // Default beam color
          })),
        };

        return clearedState;
      }),

    addNode: (coordinates) => {
      let createdNode: Node | null = null;

      set((state) => {
        //console.log("🏗️ addNode: Creating node with coordinates:", coordinates);
        //console.log("🏗️ addNode: Current nodes count:", state.nodes.length);

        // Round incoming coordinates
        const rx = Math.round(coordinates.x * 100) / 100;
        const ry = Math.round(coordinates.y * 100) / 100;
        const rz = Math.round(coordinates.z * 100) / 100;

        // Duplicate check (with tolerance from UI store if available)
        try {
          const uiStore = require("./useUIStore").useUIStore;
          const tol =
            (uiStore &&
              uiStore.getState &&
              uiStore.getState().duplicateTolerance) ||
            0.01;

          const withinTol = state.nodes.some((n) => {
            const dx = n.x - rx;
            const dy = n.y - ry;
            const dz = n.z - rz;
            const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
            return dist <= tol;
          });

          if (withinTol) {
            console.warn(
              `⚠️ addNode: Duplicate node at (${rx}, ${ry}, ${rz}) detected within tolerance ${tol}. Skipping.`
            );
            createdNode = state.nodes.find(
              (n) =>
                Math.abs(n.x - rx) <= tol &&
                Math.abs(n.y - ry) <= tol &&
                Math.abs(n.z - rz) <= tol
            )!;
            return {
              ...state,
              statusMessage: `Node already exists near (${rx}, ${ry}, ${rz}). Skipped.`,
            } as any;
          }
        } catch (err) {
          // fallback exact comparison
          const exists = state.nodes.find(
            (n) => n.x === rx && n.y === ry && n.z === rz
          );
          if (exists) {
            console.warn(
              `⚠️ addNode: Duplicate node at (${rx}, ${ry}, ${rz}) detected. Skipping.`
            );
            createdNode = exists;
            return {
              ...state,
              statusMessage: `Node already exists at (${rx}, ${ry}, ${rz}). Skipped.`,
            } as any;
          }
        }

        // Assign ID
        const nextId = findLowestAvailableNodeNumber(state.nodes);
        //console.log("🏗️ addNode: Next available node ID:", nextId);

        createdNode = {
          id: nextId.toString(),
          x: rx,
          y: ry,
          z: rz,
          isSelected: false,
          color: "#000000",
        };

        //console.log("🏗️ addNode: Created new node:", createdNode);
        //console.log(
        //   "🏗️ addNode: Total nodes after creation:",
        //   state.nodes.length + 1
        // );

        // Handle beam creation mode
        if (state.isBeamCreationMode) {
          if (!state.beamCreationFirstNodeId) {
            // First node selected, make it orange
            return {
              nodes: [...state.nodes, { ...createdNode, color: "#FFA500" }],
              nextNodeId:
                Math.max(...state.nodes.map((n) => parseInt(n.id)), nextId) + 1,
              beamCreationFirstNodeId: createdNode.id,
              isBeamCreationMode: true,
            };
          } else {
            // Second node selected → create beam
            const beam = {
              id: state.nextBeamId.toString(),
              startNodeId: state.beamCreationFirstNodeId,
              endNodeId: createdNode.id,
              material: "steel",
              isSelected: false,
              label: generateBeamLabel(state.nextBeamId),
              showLabel: state.showBeamLabels,
              color: "#4dabf7",
            };

            const updatedNodes = [...state.nodes, createdNode].map((node) => ({
              ...node,
              color: "#000000",
            }));

            return {
              nodes: updatedNodes,
              beams: [...state.beams, beam],
              nextNodeId:
                Math.max(...state.nodes.map((n) => parseInt(n.id)), nextId) + 1,
              nextBeamId: state.nextBeamId + 1,
              beamCreationFirstNodeId: null,
              isBeamCreationMode: false,
              statusMessage: "Beam created",
            };
          }
        }

        // Default (normal mode)
        return {
          nodes: [...state.nodes, createdNode],
          nextNodeId:
            Math.max(...state.nodes.map((n) => parseInt(n.id)), nextId) + 1,
        };
      });

      if (createdNode) {
        setTimeout(() => {
          const stored = localStorage.getItem("predefined_truss_presets");
          if (!stored) return;

          const predefinedTrusses: TrussPreset[] = JSON.parse(stored);
          const trussStore = useTrussStore.getState();

          predefinedTrusses.forEach((preset) => {
            const nodeIds = preset.nodeIds || [];
            if (nodeIds.includes(createdNode!.id)) {
              const setSelectedPresetName = trussStore.setSelectedPresetName;
              setSelectedPresetName(preset.name);

              trussStore.applyLoadOnNode(
                createdNode!.id,
                preset.rotationValues,
                preset.perpendicularValues
              );

              setSelectedPresetName(null);
              console.log(
                `✅ Auto-applied pending node load "${preset.name}" to node ${createdNode!.id}`
              );
            }
          });
        }, 50);
      }

      return createdNode;

      return createdNode; // always return created or existing node
    },

    clearAll: () =>
      set((state) => ({
        ...state,
        nodes: [],
        beams: [],
        nextNodeId: 1,
        nextBeamId: 1,
        beamCreationFirstNodeId: null,
        isBeamCreationMode: false,
        statusMessage: "All geometry cleared",
      })),

    deleteNode: (nodeId) =>
      set((state) => {
        const remainingNodes = state.nodes.filter((node) => node.id !== nodeId);
        const remainingBeams = state.beams.filter(
          (beam) => beam.startNodeId !== nodeId && beam.endNodeId !== nodeId
        );

        // Update nextNodeId to be one more than the highest remaining ID
        const nextNodeId =
          remainingNodes.length > 0
            ? Math.max(...remainingNodes.map((n) => parseInt(n.id))) + 1
            : 1;

        return {
          nodes: remainingNodes,
          beams: remainingBeams,
          nextNodeId,
        };
      }),

    selectNode: (nodeId) =>
      set((state) => ({
        nodes: state.nodes.map((node) => ({
          ...node,
          isSelected: node.id === nodeId,
        })),
      })),

    addBeam: (startNodeId, endNodeId) => {
      set((state) => {
        // Prevent duplicate beams (start↔end in any order)
        const exists = state.beams.some(
          (b) =>
            (b.startNodeId === startNodeId && b.endNodeId === endNodeId) ||
            (b.startNodeId === endNodeId && b.endNodeId === startNodeId)
        );

        if (exists) {
          alert("A beam between these nodes already exists!");
          return state;
        }

        const newBeam: Beam = {
          id: state.nextBeamId.toString(),
          startNodeId,
          endNodeId,
          material: "steel",
          P_ID: "-",
          isSelected: false,
          label: generateBeamLabel(state.beams.length + 1),
          showLabel: state.showBeamLabels,
          color: "#4dabf7",
        };

        return {
          beams: [...state.beams, newBeam],
          nextBeamId: state.nextBeamId + 1,
        };
      });
    },

    // KEEP THIS VERSION (around line 520-570)
    setBeams: (beams: Beam[]) => {
      // Calculate nextBeamId based on max existing beam ID to prevent duplicates
      const maxBeamId = beams.reduce((max, beam) => {
        const numId = parseInt(beam.id, 10);
        return !isNaN(numId) && numId > max ? numId : max;
      }, 0);

      set({ beams, nextBeamId: maxBeamId + 1 });

      // ✅ AUTO-APPLY: Apply pending beam loads to all imported beams
      setTimeout(() => {
        const stored = localStorage.getItem("predefined_loads");
        if (!stored) return;

        const predefinedLoads: any[] = JSON.parse(stored);
        const beamTrussStore = useBeamTrussStore.getState();

        let appliedCount = 0;

        predefinedLoads.forEach((load) => {
          const beamIds = load.beamIds || [];

          beamIds.forEach((beamId: string) => {
            const beamExists = beams.find((b) => b.id === beamId);

            if (beamExists) {
              const normalized = {
                name: load.name,
                configs: load.configs,
                id: Date.now().toString() + Math.random(),
              };

              beamTrussStore.applyPredefinedLoadToBeam(beamId, normalized);
              appliedCount++;
            }
          });
        });

        console.log(
          `✅ Auto-applied ${appliedCount} pending beam loads after bulk import`
        );
      }, 50);
    },

    /**
     * Update beams without triggering auto-apply of pending loads
     * Use this for property assignments, material changes, etc.
     * where you don't want to restore loads from localStorage
     */
    updateBeamsWithoutAutoApply: (beams: Beam[]) => {
      const maxBeamId = beams.reduce((max, beam) => {
        const numId = parseInt(beam.id, 10);
        return !isNaN(numId) && numId > max ? numId : max;
      }, 0);

      set({
        beams,
        nextBeamId: maxBeamId + 1,
      });

      console.log("✅ Updated beams without auto-apply (loads preserved)");
    },

    setNodes: (nodes: Node[]) => {
      set({ nodes });

      // AUTO-APPLY: Apply pending node loads to all imported nodes
      setTimeout(() => {
        const stored = localStorage.getItem("predefined_truss_presets");
        if (!stored) return;

        const predefinedTrusses: any[] = JSON.parse(stored);
        const trussStore = useTrussStore.getState();

        let appliedCount = 0;
        predefinedTrusses.forEach((preset) => {
          const nodeIds = preset.nodeIds || [];
          nodeIds.forEach((nodeId: string) => {
            const nodeExists = nodes.find((n) => n.id === nodeId);
            if (nodeExists) {
              const setSelectedPresetName = trussStore.setSelectedPresetName;
              setSelectedPresetName(preset.name);

              trussStore.applyLoadOnNode(
                nodeId,
                preset.rotationValues,
                preset.perpendicularValues
              );

              setSelectedPresetName(null);
              appliedCount++;
            }
          });
        });

        console.log(
          `✅ Auto-applied ${appliedCount} pending node loads after bulk import`
        );
      }, 50);
    },

    addBeamWithLabel: (startNodeId, endNodeId, label) =>
      set((state) => {
        //console.log(
        //   "🏗️ addBeamWithLabel: Creating beam between nodes:",
        //   startNodeId,
        //   "and",
        //   endNodeId
        // );
        //console.log(
        //   "🏗️ addBeamWithLabel: Current beams count:",
        //   state.beams.length
        // );

        const newBeam: Beam = {
          id: state.nextBeamId.toString(),
          startNodeId,
          endNodeId,
          material: "steel",
          isSelected: false,
          label: label || generateBeamLabel(state.beams.length + 1), // Use 1-based index
          showLabel: state.showBeamLabels,
          color: "#4dabf7", // Default beam color
        };

        //console.log("🏗️ addBeamWithLabel: Created new beam:", newBeam);
        //console.log(
        //   "🏗️ addBeamWithLabel: Total beams after creation:",
        //   state.beams.length + 1
        // );

        setTimeout(() => {
          usePropertyStore.getState().autoApplyPendingProperties();
          console.log(
            "✅ Auto-applied pending properties after addBeamWithLabel"
          );
        }, 50);

        return {
          beams: [...state.beams, newBeam],
          nextBeamId: state.nextBeamId + 1,
        };
      }),

    deleteBeam: (beamId) => {
      //console.log("🗑️ deleteBeam called with beamId:", beamId);
      //console.log(
      //   "🗑️ Current beams before deletion:",
      //   get().beams.map((b) => b.id)
      // );

      set((state) => {
        const newBeams = state.beams.filter((beam) => beam.id !== beamId);
        //console.log(
        //   "🗑️ Beams after deletion:",
        //   newBeams.map((b) => b.id)
        // );

        // Update nextBeamId to be one more than the highest remaining beam ID
        const nextBeamId =
          newBeams.length > 0
            ? Math.max(...newBeams.map((b) => parseInt(b.id, 10))) + 1
            : 1;

        return {
          beams: newBeams,
          nextBeamId,
        };
      });
    },

    selectBeam: (beamId) =>
      set((state) => {
        // Find the beam to get its connected nodes
        const beam = state.beams.find((b) => b.id === beamId);
        if (!beam) return state;

        // Get connected node IDs
        const connectedNodeIds = [beam.startNodeId, beam.endNodeId];

        // Selecting a single beam resets previous beam selection; acts like toggle to ON for that beam only
        return {
          beams: state.beams.map((b) => ({
            ...b,
            isSelected: b.id === beamId,
            color: b.id === beamId ? "#ff8c00" : "#4dabf7",
          })),
          nodes: state.nodes.map((node) => ({
            ...node,
            isSelected: connectedNodeIds.includes(node.id),
            color: connectedNodeIds.includes(node.id) ? "#ff8c00" : "#000000",
          })),
          selectedBeamIds: [beamId],
          selectedNodeIds: connectedNodeIds,
        };
      }),

    selectBeamOnly: (beamId: string) => {
      set((state) => {
        const beam = state.beams.find((b) => b.id === beamId);
        if (!beam) return state;

        // Include the beam's endpoint nodes in the selection
        const endpointNodeIds = [beam.startNodeId, beam.endNodeId];

        return {
          beams: state.beams.map((b) => ({
            ...b,
            isSelected: b.id === beamId,
            color: b.id === beamId ? "#ff8c00" : "#4dabf7",
          })),
          nodes: state.nodes.map((node) => ({
            ...node,
            isSelected: endpointNodeIds.includes(node.id),
            color: endpointNodeIds.includes(node.id) ? "#ff8c00" : "#000000",
          })),
          selectedBeamIds: [beamId],
          selectedNodeIds: endpointNodeIds,
        };
      });
    },

    // Beam center operations
    splitBeamAtCenter: (beamId) => {
      const state = get();
      const beam = state.beams.find((b) => b.id === beamId);
      if (!beam) return null;

      const getNextNodeId = () => state.nextNodeId.toString();
      const getNextBeamId = () => state.nextBeamId.toString();

      const splitResult = createNodeAtBeamCenter(
        beam,
        state.nodes,
        getNextNodeId,
        getNextBeamId,
        state.nextNodeId,
        state.nextBeamId
      );

      if (splitResult) {
        set((state) => ({
          nodes: [...state.nodes, splitResult.newNode],
          beams: [
            ...state.beams.filter((b) => b.id !== beamId),
            splitResult.beam1,
            splitResult.beam2,
          ],
          nextNodeId: state.nextNodeId + 1,
          nextBeamId: state.nextBeamId + 2,
        }));
      }

      return splitResult;
    },

    addNodeAtBeamCenter: (beamId: string) => {
      const state = get();
      const beam = state.beams.find((b) => b.id === beamId);
      if (!beam) return false;

      const getNextNodeId = () => state.nextNodeId.toString();
      const getNextBeamId = () => state.nextBeamId.toString();

      const result = createNodeAtBeamCenter(
        beam,
        state.nodes,
        getNextNodeId,
        getNextBeamId,
        state.nextNodeId,
        state.nextBeamId
      );

      if (!result) return false;

      // Update state with the split result
      set((state) => ({
        nodes: [...state.nodes, result.newNode],
        beams: [
          ...state.beams.filter((b) => b.id !== beamId),
          result.beam1,
          result.beam2,
        ],
        nextNodeId: state.nextNodeId + 1,
        nextBeamId: state.nextBeamId + 2,
      }));

      return true;
    },

    // Beam label operations
    toggleBeamLabels: () =>
      set((state) => ({
        showBeamLabels: !state.showBeamLabels,
        beams: state.beams.map((beam) => ({
          ...beam,
          showLabel: !state.showBeamLabels,
        })),
      })),

    setBeamLabel: (beamId, label) =>
      set((state) => ({
        beams: state.beams.map((beam) =>
          beam.id === beamId ? { ...beam, label } : beam
        ),
      })),

    updateBeamLabel: (beamId, label) =>
      set((state) => ({
        beams: state.beams.map((beam) =>
          beam.id === beamId ? { ...beam, label } : beam
        ),
      })),

    // Update beam properties (material, color, label, etc.)
    updateBeamProperties: (beamId, properties) =>
      set((state) => ({
        beams: state.beams.map((beam) =>
          beam.id === beamId
            ? {
                ...beam,
                ...properties, // Spread the properties to update any combination of material, color, label
              }
            : beam
        ),
      })),

    // Enhanced hover state management
    setHoveredBeam: (beamId) =>
      set(() => ({
        hoveredBeam: beamId,
      })),

    // Selection operations
    toggleNodeSelection: (nodeId) =>
      set((state) => {
        // Special handling for Prep Mode
        if (state.isPrepMode && state.referenceBeamId) {
          const isSelected = state.selectedNodeIds.includes(nodeId);
          const newSelectedNodes = isSelected
            ? state.selectedNodeIds.filter((id) => id !== nodeId)
            : [...state.selectedNodeIds, nodeId];

          // Update node colors - orange for selected nodes in Prep Mode
          return {
            selectedNodeIds: newSelectedNodes,
            nodes: state.nodes.map((node) => ({
              ...node,
              isSelected: newSelectedNodes.includes(node.id),
              color: newSelectedNodes.includes(node.id) ? "#ffa500" : "#000000",
            })),
          };
        }

        // Normal mode selection
        const isSelected = state.selectedNodeIds.includes(nodeId);
        const newSelectedNodes = isSelected
          ? state.selectedNodeIds.filter((id) => id !== nodeId)
          : [...state.selectedNodeIds, nodeId];

        // Deselect any beams whose both end-nodes are no longer selected
        const survivingBeamIds = state.selectedBeamIds.filter((id) => {
          const b = state.beams.find((bb) => bb.id === id);
          return (
            b &&
            newSelectedNodes.includes(b.startNodeId) &&
            newSelectedNodes.includes(b.endNodeId)
          );
        });

        return {
          selectedNodeIds: newSelectedNodes,
          selectedBeamIds: survivingBeamIds,
          beams: state.beams.map((b) => ({
            ...b,
            isSelected: survivingBeamIds.includes(b.id),
            color: survivingBeamIds.includes(b.id) ? "#ff8c00" : "#4dabf7",
          })),
          nodes: state.nodes.map((node) => ({
            ...node,
            isSelected: newSelectedNodes.includes(node.id),
            color: newSelectedNodes.includes(node.id) ? "#ff8c00" : "#000000",
          })),
        };
      }),

    toggleBeamSelection: (beamId) =>
      set((state) => {
        // Find the beam to get its connected nodes
        const beam = state.beams.find((b) => b.id === beamId);
        if (!beam) return state;

        // Special handling for Prep Mode
        if (state.isPrepMode) {
          // In Prep Mode, selecting a beam sets it as the reference beam
          if (state.referenceBeamId !== beamId) {
            return {
              referenceBeamId: beamId,
              selectedBeamIds: [beamId],
              selectedNodeIds: [], // Clear node selection
              beams: state.beams.map((b) => ({
                ...b,
                isSelected: b.id === beamId,
                color: b.id === beamId ? "#ffa500" : "#4dabf7", // Orange for reference beam
              })),
              nodes: state.nodes.map((node) => ({
                ...node,
                isSelected: false,
                color: "#000000", // Reset node colors
              })),
              statusMessage: "Select node(s) for perpendicular beam",
            };
          }
          return state;
        }

        // Normal mode selection
        const isCurrentlySelected = state.selectedBeamIds.includes(beamId);
        const connectedNodeIds = [beam.startNodeId, beam.endNodeId];

        let newSelectedBeamIds: string[];
        let newSelectedNodeIds: string[] = [...state.selectedNodeIds];

        if (isCurrentlySelected) {
          // --- Deselect beam ---
          newSelectedBeamIds = state.selectedBeamIds.filter(
            (id) => id !== beamId
          );

          // Remove its end-nodes unless they are still required by some other selected beam OR individually selected already
          const stillRequiredNodeIds = new Set<string>();
          newSelectedBeamIds.forEach((id) => {
            const b = state.beams.find((bb) => bb.id === id);
            if (b) {
              stillRequiredNodeIds.add(b.startNodeId);
              stillRequiredNodeIds.add(b.endNodeId);
            }
          });
          newSelectedNodeIds = newSelectedNodeIds.filter((id) =>
            stillRequiredNodeIds.has(id)
          );
        } else {
          // --- Select beam ---
          newSelectedBeamIds = [...state.selectedBeamIds, beamId];
          newSelectedNodeIds = [
            ...new Set([...newSelectedNodeIds, ...connectedNodeIds]),
          ];
        }

        return {
          beams: state.beams.map((b) => ({
            ...b,
            isSelected: newSelectedBeamIds.includes(b.id),
            color: newSelectedBeamIds.includes(b.id) ? "#ff8c00" : "#4dabf7",
          })),
          nodes: state.nodes.map((node) => ({
            ...node,
            isSelected: newSelectedNodeIds.includes(node.id),
            color: newSelectedNodeIds.includes(node.id) ? "#ff8c00" : "#000000",
          })),
          selectedBeamIds: newSelectedBeamIds,
          selectedNodeIds: newSelectedNodeIds,
        };
      }),

    clearSelection: () =>
      set((state) => ({
        selectedBeamIds: [],
        selectedNodeIds: [],
        beams: state.beams.map((beam) => ({
          ...beam,
          isSelected: false,
          color: "#4dabf7", // Reset to default blue
        })),
        nodes: state.nodes.map((node) => ({
          ...node,
          isSelected: false,
          color: "#000000", // Reset to default black
        })),
      })),

    selectMultipleNodes: (nodeIds) =>
      set((state) => ({
        selectedNodeIds: nodeIds,
        nodes: state.nodes.map((node) => ({
          ...node,
          isSelected: nodeIds.includes(node.id),
        })),
      })),

    selectMultipleBeams: (beamIds) =>
      set((state) => {
        // Collect all endpoint nodes from the NEW set of selected beams
        const newEndpointNodeIds = new Set<string>();
        beamIds.forEach((beamId) => {
          const beam = state.beams.find((b) => b.id === beamId);
          if (beam) {
            newEndpointNodeIds.add(beam.startNodeId);
            newEndpointNodeIds.add(beam.endNodeId);
          }
        });

        // Collect endpoint nodes from the PREVIOUS set of selected beams
        const oldEndpointNodeIds = new Set<string>();
        state.selectedBeamIds.forEach((beamId) => {
          const beam = state.beams.find((b) => b.id === beamId);
          if (beam) {
            oldEndpointNodeIds.add(beam.startNodeId);
            oldEndpointNodeIds.add(beam.endNodeId);
          }
        });

        // Nodes to remove: were endpoints of old beams but NOT endpoints of new beams
        // (unless they were independently selected - which we can't track, so we keep all new endpoints)
        const newSelectedNodeIds = Array.from(newEndpointNodeIds);

        return {
          selectedBeamIds: beamIds,
          selectedNodeIds: newSelectedNodeIds,
          beams: state.beams.map((beam) => ({
            ...beam,
            isSelected: beamIds.includes(beam.id),
          })),
          nodes: state.nodes.map((node) => ({
            ...node,
            isSelected: newSelectedNodeIds.includes(node.id),
            color: newSelectedNodeIds.includes(node.id) ? "#ff8c00" : "#000000",
          })),
        };
      }),

    // Copy-paste operations
    copySelected: () =>
      set((state) => {
        // Get all selected nodes
        const copiedNodes = state.nodes.filter((node) =>
          state.selectedNodeIds.includes(node.id)
        );

        // Get all selected beams AND their required nodes
        const copiedBeams = state.beams.filter((beam) =>
          state.selectedBeamIds.includes(beam.id)
        );

        // Also get nodes required by copied beams
        const requiredNodeIds = new Set<string>();
        copiedBeams.forEach((beam) => {
          requiredNodeIds.add(beam.startNodeId);
          requiredNodeIds.add(beam.endNodeId);
        });

        // Add any nodes required by beams that weren't already selected
        const additionalNodes = state.nodes.filter(
          (node) =>
            requiredNodeIds.has(node.id) &&
            !state.selectedNodeIds.includes(node.id)
        );

        const allCopiedNodes = [...copiedNodes, ...additionalNodes];

        //console.log("📋 Copied elements:", {
        //   nodes: allCopiedNodes.length,
        //   beams: copiedBeams.length,
        // });

        return {
          copiedNodes: allCopiedNodes,
          copiedBeams,
        };
      }),

    // Modify paste operation to restore colors
    pasteWithOffset: (params: PasteParams) => {
      const { copiedNodes, copiedBeams } = get();

      if (copiedNodes.length === 0 && copiedBeams.length === 0) {
        return;
      }

      // Helper function to paste a single copy with given offsets
      const pasteSingleCopy = (
        selectedNodes: Node[],
        selectedBeams: Beam[],
        deltaX: number,
        deltaY: number,
        deltaZ: number
      ) => {
        const { nodes, beams, nextNodeId, nextBeamId } = get();

        // Create a map to track old node IDs to new node IDs
        const nodeIdMap = new Map<string, string>();

        // Add nodes with offset
        selectedNodes.forEach((node) => {
          const newNode = {
            ...node,
            id: `N${nextNodeId + nodes.length}`,
            x: node.x + deltaX,
            y: node.y + deltaY,
            z: node.z + deltaZ,
          };
          nodeIdMap.set(node.id, newNode.id);
          nodes.push(newNode);
        });

        // Add beams with updated node references
        selectedBeams.forEach((beam) => {
          const newBeam = {
            ...beam,
            id: `B${nextBeamId + beams.length}`,
            startNodeId: nodeIdMap.get(beam.startNodeId) || beam.startNodeId,
            endNodeId: nodeIdMap.get(beam.endNodeId) || beam.endNodeId,
          };
          beams.push(newBeam);
        });

        set({
          nodes,
          beams,
          nextNodeId: nextNodeId + selectedNodes.length,
          nextBeamId: nextBeamId + selectedBeams.length,
        });
      };

      // Main paste function that handles multiple copies
      const { copies } = { copies: 1 }; // Default to 1 copy if not specified
      let copyIndex = 1;
      while (copyIndex <= copies) {
        pasteSingleCopy(
          copiedNodes,
          copiedBeams,
          copyIndex * params.x,
          copyIndex * params.y,
          copyIndex * params.z
        );
        copyIndex++;
      }

      // Restore colors after all copies are made
      get().restoreDefaultColors();
      //console.log(
      //   `Created ${copies} copies with offsets: ΔX=${params.x}, ΔY=${params.y}, ΔZ=${params.z}`
      // );
    },

    // New 3D beam operations
    getBeamMidpoint3D: (beamId) => {
      const state = get();
      const beam = state.beams.find((b) => b.id === beamId);
      if (!beam) return null;

      const startNode = state.nodes.find((n) => n.id === beam.startNodeId);
      const endNode = state.nodes.find((n) => n.id === beam.endNodeId);

      if (!startNode || !endNode) return null;

      return {
        x: (startNode.x + endNode.x) / 2,
        y: (startNode.y + endNode.y) / 2,
        z: (startNode.z + endNode.z) / 2,
      };
    },

    findBeamAtPosition3D: (position, tolerance = 0.5) => {
      const state = get();

      return (
        state.beams.find((beam) => {
          const startNode = state.nodes.find((n) => n.id === beam.startNodeId);
          const endNode = state.nodes.find((n) => n.id === beam.endNodeId);

          if (!startNode || !endNode) return false;

          // Calculate distance from point to line segment in 3D
          const dx = endNode.x - startNode.x;
          const dy = endNode.y - startNode.y;
          const dz = endNode.z - startNode.z;

          const px = position.x - startNode.x;
          const py = position.y - startNode.y;
          const pz = position.z - startNode.z;

          const dot = px * dx + py * dy + pz * dz;
          const lenSq = dx * dx + dy * dy + dz * dz;

          if (lenSq === 0) return false;

          const param = Math.max(0, Math.min(1, dot / lenSq));

          const closestX = startNode.x + param * dx;
          const closestY = startNode.y + param * dy;
          const closestZ = startNode.z + param * dz;

          const distance = Math.sqrt(
            Math.pow(position.x - closestX, 2) +
              Math.pow(position.y - closestY, 2) +
              Math.pow(position.z - closestZ, 2)
          );

          return distance <= tolerance;
        }) || null
      );
    },

    divideBeamAtMidpoint: (beamId: string): boolean => {
      const state = get();

      // ✅ ENHANCED DEBUGGING - Show exact types and values
      console.log("🔍 === DIVIDE BEAM DEBUG START ===");
      console.log("📥 Input beamId:", beamId, "| Type:", typeof beamId);
      console.log("📊 Total beams in store:", state.beams.length);

      // Log all beam IDs with their types
      console.log("📋 All beam IDs in store:");
      state.beams.forEach((b) => {
        console.log(`  - Beam ID: "${b.id}" (type: ${typeof b.id})`);
      });

      // ✅ FIX: Normalize beamId to string
      const targetBeamId = String(beamId);
      console.log(
        "🎯 Normalized target ID:",
        targetBeamId,
        "| Type:",
        typeof targetBeamId
      );

      // ✅ FIX: Use normalized string comparison
      const beam = state.beams.find((b) => String(b.id) === targetBeamId);

      if (!beam) {
        console.error("❌ Beam not found:", targetBeamId);
        console.log(
          "🔍 Available beams:",
          state.beams.map((b) => ({ id: b.id, type: typeof b.id }))
        );
        console.log("🔍 Comparison results:");
        state.beams.forEach((b) => {
          console.log(
            `  - "${b.id}" === "${targetBeamId}": ${String(b.id) === targetBeamId}`
          );
          console.log(`  - Raw comparison: ${b.id === beamId}`);
        });
        return false;
      }

      console.log("✅ Found beam:", {
        id: beam.id,
        startNodeId: beam.startNodeId,
        endNodeId: beam.endNodeId,
      });

      const startNode = state.nodes.find(
        (n) => String(n.id) === String(beam.startNodeId)
      );
      const endNode = state.nodes.find(
        (n) => String(n.id) === String(beam.endNodeId)
      );

      if (!startNode || !endNode) {
        console.error(
          "❌ Nodes not found - Start:",
          beam.startNodeId,
          "End:",
          beam.endNodeId
        );
        console.log(
          "🔍 Available nodes:",
          state.nodes.map((n) => ({ id: n.id, type: typeof n.id }))
        );
        return false;
      }

      console.log("✅ Found nodes - Start:", startNode.id, "End:", endNode.id);

      // Calculate midpoint
      const midpoint = {
        x: (startNode.x + endNode.x) / 2,
        y: (startNode.y + endNode.y) / 2,
        z: (startNode.z + endNode.z) / 2,
      };

      console.log("📍 Calculated midpoint:", midpoint);

      // Create new node at midpoint
      const newNodeId = state.nextNodeId.toString();
      const newNode: Node = {
        id: newNodeId,
        x: midpoint.x,
        y: midpoint.y,
        z: midpoint.z,
        isSelected: false,
        color: "#000000",
      };

      console.log("🆕 Creating new node:", newNode);

      // Create two new beams
      const beam1: Beam = {
        ...beam,
        id: state.nextBeamId.toString(),
        endNodeId: newNodeId,
        label: generateBeamLabel(state.nextBeamId),
        color: "#4dabf7",
      };

      const beam2: Beam = {
        ...beam,
        id: (state.nextBeamId + 1).toString(),
        startNodeId: newNodeId,
        label: generateBeamLabel(state.nextBeamId + 1),
        color: "#4dabf7",
      };

      console.log("🆕 Creating beam1:", {
        id: beam1.id,
        start: beam1.startNodeId,
        end: beam1.endNodeId,
      });
      console.log("🆕 Creating beam2:", {
        id: beam2.id,
        start: beam2.startNodeId,
        end: beam2.endNodeId,
      });

      // Update state
      set((state) => ({
        nodes: [...state.nodes, newNode],
        beams: [
          ...state.beams.filter((b) => String(b.id) !== targetBeamId),
          beam1,
          beam2,
        ],
        nextNodeId: state.nextNodeId + 1,
        nextBeamId: state.nextBeamId + 2,
        hoveredBeam: null,
      }));

      console.log("✅ State updated - Old beam removed, 2 new beams added");
      console.log(
        "📊 New totals - Nodes:",
        get().nodes.length,
        "Beams:",
        get().beams.length
      );

      // Auto-apply pending properties
      setTimeout(() => {
        usePropertyStore.getState().autoApplyPendingProperties();
        console.log("✅ Auto-applied pending properties after beam division");
      }, 50);

      console.log("🔍 === DIVIDE BEAM DEBUG END ===");
      return true;
    },

    // Node on Beam operations
    addNodeOnBeam: (clickPoint, tolerance = 1.0) => {
      const state = get();
      //console.log("🎯 Adding node on beam at point:", clickPoint);

      // Find the closest beam to the click point
      const beamResult = findBeamAtPosition3D(
        clickPoint,
        state.beams,
        state.nodes,
        tolerance
      );

      if (!beamResult) {
        //console.log("❌ No beam found at click position");
        return false;
      }

      const { beam, positionResult } = beamResult;
      //console.log(
      //   "✅ Found beam:",
      //   beam.id,
      //   "at parameter:",
      //   positionResult.parameter
      // );

      if (!positionResult.isValid) {
        //console.log("❌ Invalid position result");
        return false;
      }

      // Don't create node too close to existing nodes (avoid duplicates)
      const minDistanceToNodes = 0.1;
      const tooCloseToExisting = state.nodes.some((node) => {
        const distance = Math.sqrt(
          Math.pow(node.x - positionResult.position.x, 2) +
            Math.pow(node.y - positionResult.position.y, 2) +
            Math.pow(node.z - positionResult.position.z, 2)
        );
        return distance < minDistanceToNodes;
      });

      if (tooCloseToExisting) {
        //console.log("❌ Too close to existing node");
        return false;
      }

      // Create the beam split using our utility function
      const splitResult = createNodeAtBeamPosition(
        beam,
        state.nodes,
        positionResult.position,
        positionResult.parameter,
        () => state.nextNodeId.toString(),
        () => state.nextBeamId.toString(),
        state.nextNodeId,
        state.nextBeamId
      );

      if (!splitResult) {
        //console.log("❌ Failed to create beam split");
        return false;
      }

      //console.log("✅ Created beam split:", splitResult);

      // Update the store state
      set((state) => {
        // Find the index of the original beam
        const originalBeamIndex = state.beams.findIndex(
          (b) => b.id === beam.id
        );

        // Create new beams array with split beams inserted at original position
        const newBeams = [...state.beams];
        newBeams.splice(
          originalBeamIndex,
          1, // Remove original beam
          {
            ...splitResult.beam1,
            isNewlyCreated: true, // Add flag for highlighting
          },
          {
            ...splitResult.beam2,
            isNewlyCreated: true, // Add flag for highlighting
          }
        );

        return {
          nodes: [
            ...state.nodes,
            {
              ...splitResult.newNode,
              isNewlyCreated: true, // Add flag for highlighting
            },
          ],
          beams: newBeams,
          nextNodeId: state.nextNodeId + 1,
          nextBeamId: state.nextBeamId + 2,
          hoveredBeam: null, // Clear hover state
        };
      });

      //console.log("✅ Node on beam operation completed successfully!");

      // Remove the "newly created" flag after 3 seconds
      setTimeout(() => {
        set((state) => ({
          nodes: state.nodes.map((node) => ({
            ...node,
            isNewlyCreated: false,
          })),
          beams: state.beams.map((beam) => ({
            ...beam,
            isNewlyCreated: false,
          })),
        }));
      }, 3000);

      return true;
    },

    /**
     * Create perpendicular beams from selected nodes to a reference beam.
     * @param referenceBeamId The ID of the reference beam.
     * @param nodeIds Array of node IDs to connect perpendicularly.
     * @returns Array of created Beam objects.
     */
    createPerpendicularBeams: (referenceBeamId: string, nodeIds: string[]) => {
      //console.log("🎯 createPerpendicularBeams called with:", {
      //   referenceBeamId,
      //   nodeIds,
      // });

      const state = get();
      //console.log("🎯 Current state:", {
      //   nodesCount: state.nodes.length,
      //   beamsCount: state.beams.length,
      //   nextNodeId: state.nextNodeId,
      //   nextBeamId: state.nextBeamId,
      // });

      const referenceBeam = state.beams.find((b) => b.id === referenceBeamId);
      if (!referenceBeam) {
        //console.log("❌ Reference beam not found:", referenceBeamId);
        return [];
      }

      const startNode = state.nodes.find(
        (n) => n.id === referenceBeam.startNodeId
      );
      const endNode = state.nodes.find((n) => n.id === referenceBeam.endNodeId);
      if (!startNode || !endNode) {
        //console.log("❌ Start or end node not found:", {
        //   startNodeId: referenceBeam.startNodeId,
        //   endNodeId: referenceBeam.endNodeId,
        // });
        return [];
      }

      //console.log("✅ Found reference beam and nodes:", {
      //   referenceBeam,
      //   startNode,
      //   endNode,
      // });

      const createdBeams = [];
      const newBeams = [...state.beams];
      const newNodes = [...state.nodes];
      let nextBeamId = state.nextBeamId;

      // Helper: project a point onto a line segment in 3D
      function projectPointOntoLineSegment(
        point: Node,
        lineStart: Node,
        lineEnd: Node
      ): { x: number; y: number; z: number; t: number } {
        const px = point.x,
          py = point.y,
          pz = point.z;
        const ax = lineStart.x,
          ay = lineStart.y,
          az = lineStart.z;
        const bx = lineEnd.x,
          by = lineEnd.y,
          bz = lineEnd.z;
        const abx = bx - ax,
          aby = by - ay,
          abz = bz - az;
        const apx = px - ax,
          apy = py - ay,
          apz = pz - az;
        const abLenSq = abx * abx + aby * aby + abz * abz;
        if (abLenSq === 0) return { x: ax, y: ay, z: az, t: 0 };
        const t = (apx * abx + apy * aby + apz * abz) / abLenSq;
        return {
          x: ax + Math.max(0, Math.min(1, t)) * abx,
          y: ay + Math.max(0, Math.min(1, t)) * aby,
          z: az + Math.max(0, Math.min(1, t)) * abz,
          t,
        };
      }

      const commonProjections: Record<
        string,
        { x: number; y: number; z: number; usedBy: string[] }
      > = {};

      // First pass: collect all projection points
      for (const nodeId of nodeIds) {
        //console.log("🎯 Processing node:", nodeId);

        const node = newNodes.find((n) => n.id === nodeId);
        if (!node) {
          //console.log("❌ Node not found:", nodeId);
          continue;
        }

        //console.log("✅ Found target node:", node);

        // Find perpendicular point on the reference beam
        const perpResult = projectPointOntoLineSegment(
          node,
          startNode,
          endNode
        );
        //console.log("🎯 Perpendicular projection result:", perpResult);

        // Only allow if projection is strictly within the segment (0 < t < 1)
        if (perpResult.t <= 0 || perpResult.t >= 1) {
          //console.log("❌ Projection outside beam segment, t =", perpResult.t);
          continue;
        }

        const perpPoint = { x: perpResult.x, y: perpResult.y, z: perpResult.z };
        //console.log("✅ Valid perpendicular point:", perpPoint);

        // Check if the perpendicular point is already recorded as a common projection
        const perpKey = `${perpPoint.x.toFixed(6)},${perpPoint.y.toFixed(6)},${perpPoint.z.toFixed(6)}`;
        if (!commonProjections[perpKey]) {
          commonProjections[perpKey] = { ...perpPoint, usedBy: [] };
        }
        commonProjections[perpKey].usedBy.push(node.id);
      }

      // Create nodes and beams only for unique common projections
      for (const projectionKey in commonProjections) {
        const projection = commonProjections[projectionKey];

        let perpNode = newNodes.find(
          (n) =>
            Math.abs(n.x - projection.x) < 1e-6 &&
            Math.abs(n.y - projection.y) < 1e-6 &&
            Math.abs(n.z - projection.z) < 1e-6
        );

        if (!perpNode) {
          const newNodeId = (
            state.nextNodeId +
            (newNodes.length - state.nodes.length)
          ).toString();
          const newNode: Node = {
            id: newNodeId,
            x: projection.x,
            y: projection.y,
            z: projection.z,
            isSelected: false,
            color: "#4dabf7",
          };
          //console.log("🆕 Creating new intersection node:", newNode);
          newNodes.push(newNode);
          perpNode = newNode;
        } else {
          //console.log("✅ Using existing intersection node:", perpNode);
        }

        // Create beams from each node that shares this projection
        for (const startNodeId of projection.usedBy) {
          // Check if a beam already exists between these nodes
          const beamExists = newBeams.some(
            (beam) =>
              (beam.startNodeId === startNodeId &&
                beam.endNodeId === perpNode.id) ||
              (beam.startNodeId === perpNode.id &&
                beam.endNodeId === startNodeId)
          );

          if (beamExists) {
            //console.log(
            //   "⚠️ Beam already exists between nodes:",
            //   startNodeId,
            //   "and",
            //   perpNode.id,
            //   "- skipping"
            // );
            continue;
          }

          const newBeam: Beam = {
            id: nextBeamId.toString(),
            startNodeId,
            endNodeId: perpNode.id,
            material: "steel",
            P_ID: "-", // No section property assigned by default
            isSelected: false,
            label: `P${nextBeamId}`,
            showLabel: true,
            color: "#ff9800",
          };

          //console.log("🆕 Creating perpendicular beam:", newBeam);
          newBeams.push(newBeam);
          createdBeams.push(newBeam);
          nextBeamId++;
        }
      }

      // ========================================
      // SPLIT REFERENCE BEAM AT ALL NEW NODES
      // ========================================
      // Collect all NEW nodes that were created ON the reference beam (not existing nodes)
      const newNodesOnRefBeam = Object.values(commonProjections)
        .map((proj) =>
          newNodes.find(
            (n) =>
              Math.abs(n.x - proj.x) < 1e-6 &&
              Math.abs(n.y - proj.y) < 1e-6 &&
              Math.abs(n.z - proj.z) < 1e-6
          )
        )
        .filter(
          (n): n is Node =>
            n !== undefined && !state.nodes.find((sn) => sn.id === n.id)
        );

      if (newNodesOnRefBeam.length > 0) {
        // Sort nodes along the beam direction (by parameter t)
        const sortedNodes = newNodesOnRefBeam
          .map((node) => {
            const proj = projectPointOntoLineSegment(node, startNode, endNode);
            return { node, t: proj.t };
          })
          .sort((a, b) => a.t - b.t);

        // Remove the original reference beam
        const refBeamIndex = newBeams.findIndex(
          (b) => b.id === referenceBeamId
        );
        let removedRefBeam: Beam | null = null;
        if (refBeamIndex !== -1) {
          removedRefBeam = newBeams.splice(refBeamIndex, 1)[0];
        }

        // Create split beams: start -> first node, first node -> second node, ..., last node -> end
        const splitPoints = [
          { id: referenceBeam.startNodeId },
          ...sortedNodes.map((s) => ({ id: s.node.id })),
          { id: referenceBeam.endNodeId },
        ];

        for (let i = 0; i < splitPoints.length - 1; i++) {
          const splitBeam: Beam = {
            id: nextBeamId.toString(),
            startNodeId: splitPoints[i].id,
            endNodeId: splitPoints[i + 1].id,
            material: removedRefBeam?.material || "steel",
            P_ID: removedRefBeam?.P_ID || "-",
            isSelected: false,
            label: `B${nextBeamId}`,
            showLabel: true,
            color: removedRefBeam?.color || "#4dabf7",
          };
          newBeams.push(splitBeam);
          nextBeamId++;
          console.log(
            `🔀 Split beam created: ${splitBeam.id} (${splitPoints[i].id} → ${splitPoints[i + 1].id})`
          );
        }

        console.log(
          `✅ Reference beam ${referenceBeamId} split into ${splitPoints.length - 1} segments`
        );
      }

      // Update state with all changes at once
      set({
        beams: newBeams,
        nextBeamId,
        nodes: newNodes,
        nextNodeId: state.nextNodeId + (newNodes.length - state.nodes.length),
      });

      setTimeout(() => {
        usePropertyStore.getState().autoApplyPendingProperties();
        console.log(
          "✅ Auto-applied pending properties after perpendicular beams"
        );
      }, 50);

      //console.log("✅ State updated successfully");
      return createdBeams;
    },

    // Implement missing properties from interface
    selectedElements: [],
    selectElement: (id) =>
      set((state) => ({
        selectedElements: state.selectedElements.includes(id)
          ? state.selectedElements.filter((elemId) => elemId !== id)
          : [...state.selectedElements, id],
      })),
    updateNode: (nodeId, updates) => {
      set((state) => ({
        nodes: state.nodes.map((node) =>
          node.id === nodeId ? { ...node, ...updates } : node
        ),
      }));
      // Trigger beam regeneration so connected beams update in Three.js
      try {
        const { regenerateBeams } = get();
        regenerateBeams(nodeId);
      } catch {}
    },
    updateElement: (elementId, updates) =>
      set((state) => ({
        beams: state.beams.map((beam) =>
          beam.id === elementId ? { ...beam, ...updates } : beam
        ),
      })),

    // Beam regeneration after node movement
    regenerateBeams: (movedNodeId?: string) => {
      const state = get();

      // For now, just log the regeneration
      // TODO: Implement collinearity checks and beam reconnection
      // This will be enhanced in future iterations

      const movedNode = movedNodeId
        ? state.nodes.find((n) => n.id === movedNodeId)
        : null;
      if (movedNode) {
        //console.log("📍 Moved node position:", {
        //   x: movedNode.x,
        //   y: movedNode.y,
        //   z: movedNode.z,
        // });
      }

      // Check for potential beam connections
      const connectedBeams = movedNodeId
        ? state.beams.filter(
            (b) => b.startNodeId === movedNodeId || b.endNodeId === movedNodeId
          )
        : [];

      //console.log("🔗 Connected beams:", connectedBeams.length);

      // Future implementation will include:
      // 1. Check if moved node aligns with existing beam trajectories
      // 2. Create new beam connections if nodes become collinear
      // 3. Maintain beam properties during reconnection
      // 4. Update beam length calculations

      return {
        success: true,
        message: "Beam regeneration completed",
        connectedBeams: connectedBeams.length,
      };
    },
  };
});

// Subscribe to Zustand store and publish to centralized store
// This keeps the centralized store in sync with all model data
useModelStore.subscribe((state) => {
  addData("nodes", state.nodes);
  addData("beams", state.beams);
  // console.log(
  //   "✅ [Zustand→Centralized] Published nodes:",
  //   state.nodes.length,
  //   "beams:",
  //   state.beams.length
  // );
});
